import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { computed, inject, onMounted, ref, type SetupContext } from "vue";
import { type IValidationErrors } from "../../abstractions/IValidationErrors.ts";
import type { PartyAddressDto } from "../../shared/PartyAddressDto.ts";
import type { CartDto } from "../../shared/CartDto.ts";
import type { CourseDto } from "../../shared/CourseDto.ts";
import type { CustomerMeDto } from "../../shared/CustomerMeDto.ts";
import type { AddressInput } from "../../shared/AddressDtos.ts";
import type { LoginService } from "../../application/services/LoginService.ts";
import { CustomerService } from "../../application/services/CustomerService.ts";
import PhoneNumberEntry from "../PhoneNumberEntry/index.ts";
import AddressEntry from "../AddressEntry/index.ts";
import CustomerSummaryCard from "../CustomerSummaryCard/index.ts";

interface Props {
  cart: CartDto;
  course: CourseDto;
  me: CustomerMeDto | null;
  guestEmail: string;
  validationErrors: IValidationErrors;
}

export function emptyAddress(): PartyAddressDto {
  return {
    email: "",
    firstName: "",
    lastName: "",
    streetName: "",
    buildingNumber: "",
    address2: "",
    countryCode: "CH",
    zipCode: "",
    city: "",
    phoneP: "",
    mobile: "",
    phoneB: "",
    birthdate: null,
  };
}

// Schritt 3: Angaben des Bestellers. Eingeloggt: Karte "Deine Angaben" (bestaetigen oder aendern).
// Neu/Gast: Bloecke E-Mail (mit Bekannt-Pruefung), Name, Adresse (Assistent), Telefon.
export default {
  template: html,
  components: { PhoneNumberEntry, AddressEntry, CustomerSummaryCard },
  emits: {
    onChange: (value: PartyAddressDto) => typeof value === "object",
    onConfirm: () => true,
    onWantLogin: (email: string) => typeof email === "string",
  },
  props: {
    cart: { type: Object, required: true },
    course: { type: Object, required: true },
    me: { type: Object, required: false, default: null },
    guestEmail: { type: String, required: false, default: "" },
    validationErrors: { type: Object, required: true },
  },
  setup(props: Props, { emit }: SetupContext) {
    const loginService: LoginService | undefined = inject("loginService");
    const model = ref<PartyAddressDto>(emptyAddress());
    const editing = ref(false);
    const emailKnown = ref(false);
    const localError = ref("");
    let lastCheckedEmail = "";

    const isLoggedIn = computed(() => props.me != null);
    const showForm = computed(() => !isLoggedIn.value || editing.value);

    const address = computed<AddressInput>({
      get: () => ({
        countryCode: model.value.countryCode || "CH",
        streetName: model.value.streetName || "",
        buildingNumber: model.value.buildingNumber || "",
        zipCode: model.value.zipCode || "",
        city: model.value.city || "",
      }),
      set: (a: AddressInput) => {
        model.value = { ...model.value, ...a };
      },
    });

    const getError = function (key: string): string {
      const keyInApi = key.charAt(0).toUpperCase() + key.slice(1);
      if (props.validationErrors && keyInApi in props.validationErrors) {
        return props.validationErrors[keyInApi].join(", ");
      }
      return "";
    };

    // Gast: E-Mail bekannt? Dann Login anbieten (nur Hinweis, nie Zwang)
    const checkEmail = async function () {
      const email = (model.value.email || "").trim();
      if (isLoggedIn.value || !loginService || email == lastCheckedEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return;
      }
      lastCheckedEmail = email;
      try {
        emailKnown.value = await loginService.isEmailKnown(email);
      } catch {
        emailKnown.value = false;
      }
    };

    const wantLogin = function () {
      emit("onWantLogin", (model.value.email || "").trim());
    };

    const save = function () {
      localError.value = "";
      if (!(model.value.mobile || "").trim() && !(model.value.phoneP || "").trim()) {
        localError.value = "Bitte eine Mobil- oder Festnetznummer angeben, damit wir dich bei Änderungen erreichen.";
        return;
      }
      emit("onChange", model.value);
    };

    const confirm = () => emit("onConfirm");
    const edit = function () {
      if (props.me) {
        model.value = CustomerService.toPartyAddress(props.me);
      }
      editing.value = true;
    };

    onMounted(function () {
      if (props.cart != null && props.cart.customerAddress != null) {
        model.value = { ...emptyAddress(), ...props.cart.customerAddress };
      } else if (props.me) {
        model.value = CustomerService.toPartyAddress(props.me);
      }
      if (!isLoggedIn.value && props.guestEmail && !(model.value.email || "").trim()) {
        model.value.email = props.guestEmail;
      }
      if (isLoggedIn.value && props.cart?.customerAddress) {
        // Kunde hatte schon Aenderungen erfasst: direkt im Formular weitermachen
        editing.value = true;
      }
    });

    return {
      props,
      model,
      address,
      editing,
      emailKnown,
      localError,
      isLoggedIn,
      showForm,
      getError,
      checkEmail,
      wantLogin,
      save,
      confirm,
      edit,
    };
  },
};
